import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { ModalSingleInput } from '@/lib/v2/examples';

import {
  TRANSLATE_MODAL,
  TRANSLATE_MODAL_INPUTS,
} from '@/src/modules/CampaignsModule/components/CampaignsModal/CreateEditCampaigns/constants';

import {
  atomRefetchAllData,
  atomSelectedActionsRowsWithID,
  atomSelectedRowActionName,
} from '@/modules/CampaignsModule/atoms/campaignAtom';
import { atomToggleEditActionsModal } from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { useCampaignActionsStateContext } from '@/modules/CampaignsModule/contexts/CampaignActionsContext';
import { useCampaignActions } from '@/modules/CampaignsModule/hooks/useCampaignActions';
import { EMaxMinLength } from '@/modules/CampaignsModule/interfaces/Campaigns';

// eslint-disable-next-line regexp/no-obscure-range
const REGEX_INPUT = /^[\dA-Za-zÀ-ÖØ-öø-ÿ\s\-,'.!¡¿?()\\"&$%]+$/u;

const EditActions = () => {
  const { t } = useTranslation();
  const { id } = useParams();

  const { filterBy, orderBy, pageIndex, pageSize } = useCampaignActionsStateContext();
  const { editAction, loading } = useCampaignActions({
    campaignID: Number(id),
    campaignActionsStateContext: { filterBy, orderBy, pageIndex, pageSize },
  });

  const actionId = useAtomValue(atomSelectedActionsRowsWithID);
  const actionName = useAtomValue(atomSelectedRowActionName);
  const setRefetchAllData = useSetAtom(atomRefetchAllData);
  const [toggleEditActionsModal, setToggleEditActionsModal] = useAtom(atomToggleEditActionsModal);

  const VALIDATION_RULES = {
    name: {
      pattern: {
        value: REGEX_INPUT,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.pattern`),
      },
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: EMaxMinLength.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          min: EMaxMinLength.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: EMaxMinLength.NAME_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          max: EMaxMinLength.NAME_MAX,
        }),
      },
    },
  };

  const handleCloseModal = useCallback(() => {
    setToggleEditActionsModal(false);
  }, [setToggleEditActionsModal]);

  const handleSubmitData = useCallback(
    async (nameValue: string) => {
      await editAction({ name: nameValue, actionId: Number(actionId) });
      setRefetchAllData(true);
      handleCloseModal();
    },
    [actionId, editAction, handleCloseModal, setRefetchAllData]
  );

  return (
    <ModalSingleInput
      id="edit-action-table"
      isLoading={loading}
      isOpen={toggleEditActionsModal}
      rules={VALIDATION_RULES.name}
      title={t('CAMPAIGN_ACTIONS_MAIN.EDIT_ACTIONS.title')}
      value={actionName}
      onClose={handleCloseModal}
      onSubmit={handleSubmitData}
    />
  );
};

export default EditActions;
